import { useEffect, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ArrowForward, NoteAdd } from "@mui/icons-material";
import Loader from "../../Loader";
import NoteDialog from "../NoteDialog";
import MobileBottomNav from "./MobileBottomNav";
import MobileWeekSummary from "./MobileWeekSummary";
import MobileExerciseCard from "./MobileExerciseCard";
import MobileActionButtons from "./MobileActionButtons";
import { getWorkout, updateProgram, finishWorkout, resetWeek, rerunManipulation } from "../../../services/PlanSerice";
import { getErrorMessage } from "../../../enums/ErrorMessages";

const MobileClientWeekView = ({
  planState,
  setPlanState,
  activeTab,
  setActiveTab,
  editMode,
  setEditMode,
  openIntervals,
  setOpenIntervals,
  reloadKey,
  setReloadKey,
  loading,
  setLoading,
  newNote,
  setNewNote,
  handleNewNote,
  handleForward,
  handleBackwards,
  params,
  navigate,
  notificationHandler,
  fetchPlan
}) => {
  const [workout, setWorkout] = useState(null);
  const [prvWorkout, setPrvWorkout] = useState(null);
  const [reload, setReload] = useState(false);

  // Get current week's workouts
  const getCurrentWorkouts = () => {
    if (!planState?.plan?.blocks) return [];
    const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curBlock = blocks.at(planState.blockIdx);
    if (!curBlock?.weeks) return [];
    const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curWeek = weeks.at(planState.weekIdx);
    return curWeek?.workouts?.sort((a, b) => a.index > b.index ? 1 : -1) || [];
  };

  const getPrvWorkoutId = () => {
    if (!planState?.plan?.blocks) return null;
    const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curBlock = blocks.at(planState.blockIdx);
    if (!curBlock?.weeks) return null;

    const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curWeek = weeks.at(planState.weekIdx);

    const workoutIdx = activeTab - 1;

    if (curWeek.index > 0) {
      return weeks.at(curWeek.index - 1)?.workouts?.sort((a, b) => a.index > b.index ? 1 : -1).at(workoutIdx)?.id;
    } else if (curBlock.index > 0) {
      const prvBlock = blocks.at(curBlock.index - 1);
      const prvBlockWeeks = prvBlock?.weeks?.sort((a, b) => a.index > b.index ? 1 : -1);
      const lastWeek = prvBlockWeeks?.at(prvBlockWeeks.length - 1);
      return lastWeek?.workouts?.sort((a, b) => a.index > b.index ? 1 : -1).at(workoutIdx)?.id;
    }
    return null;
  };

  // Fetch workout data when planState or activeTab changes
  useEffect(() => {
    if (!planState?.plan?.blocks) return;

    setLoading(true);
    const workouts = getCurrentWorkouts();
    const workoutIdx = activeTab - 1;
    const currentWorkout = workouts[workoutIdx];

    if (!currentWorkout) {
      setLoading(false);
      return;
    }

    getWorkout(currentWorkout.id)
      .then(res => {
        setWorkout(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    const prvWorkoutId = getPrvWorkoutId();
    if (prvWorkoutId) {
      getWorkout(prvWorkoutId).then(res => setPrvWorkout(res.data));
    } else {
      setPrvWorkout(null);
    }
  }, [planState, activeTab, reload]);

  const isAnyWorkoutCompletedOrWeekFinished = () => {
    if (!planState?.plan?.blocks) return false;
    const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curBlock = blocks.at(planState.blockIdx);
    if (!curBlock?.weeks) return false;
    const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curWeek = weeks.at(planState.weekIdx);
    if (!curWeek?.workouts) return false;
    return curWeek.workouts.some(w => w.stateType === "COMPLETED") || curWeek.finished;
  };

  const isLastWeekFinished = () => {
    if (planState.blockIdx === 0 && planState.weekIdx === 0) return true;
    if (!planState?.plan?.blocks) return false;

    const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);

    if (planState.weekIdx === 0) {
      const prvBlock = blocks.at(planState.blockIdx - 1);
      if (!prvBlock?.weeks) return false;
      const prvWeek = prvBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1).at(prvBlock.weeks.length - 1);
      return prvWeek?.finished;
    } else {
      const curBlock = blocks.at(planState.blockIdx);
      const prvWeek = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1).at(planState.weekIdx - 1);
      return prvWeek?.finished;
    }
  };

  const getCurrentWeek = () => {
    if (!planState?.plan?.blocks) return null;
    const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curBlock = blocks.at(planState.blockIdx);
    if (!curBlock?.weeks) return null;
    return curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1).at(planState.weekIdx);
  };

  const validateWorkout = () => {
    const errors = [];
    workout?.programExercises?.forEach((exercise, index) => {
      const exerciseNum = index + 1;
      if (!exercise.restTime && exercise.restTime !== 0) {
        errors.push(`תרגיל ${exerciseNum}: חסר זמן מנוחה`);
      }
      if (exercise.targetMuscle !== 'CARDIO') {
        if (!exercise.intervals || exercise.intervals.length === 0) {
          errors.push(`תרגיל ${exerciseNum}: חסרים סטים`);
        }
      }
    });
    return errors;
  };

  const handleSave = () => {
    const validationErrors = validateWorkout();
    if (validationErrors.length > 0) {
      notificationHandler.error(validationErrors.join('\n'));
      return;
    }

    setLoading(true);
    updateProgram({ ...workout, traineeId: params.userId })
      .then(() => setReload(prv => !prv))
      .then(() => setEditMode(false))
      .then(() => setReloadKey(prev => prev + 1))
      .then(() => notificationHandler.success("תוכנית נשמרה בהצלחה"))
      .then(() => setLoading(false))
      .catch(e => {
        notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
        setLoading(false);
      });
  };

  const handleFinishWorkout = () => {
    if (!workout?.id) return;
    finishWorkout(workout.id)
      .then(() => setReload(prv => !prv))
      .then(() => setEditMode(false))
      .then(() => {
        if (fetchPlan) fetchPlan();
      })
      .then(() => notificationHandler.success("אימון סויים בהצלחה"));
  };

  const handleResetWeek = () => {
    const curWeek = getCurrentWeek();
    if (!curWeek?.id) return;
    resetWeek(curWeek.id)
      .then(() => setReload(prv => !prv))
      .then(() => setEditMode(false))
      .then(() => notificationHandler.success("שבוע אופס בהצלחה"));
  };

  const handleRerunManipulation = () => {
    if (!planState?.plan?.blocks) return;

    const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curBlock = blocks.at(planState.blockIdx);
    if (!curBlock?.weeks) return;

    const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
    const curWeek = weeks.at(planState.weekIdx);
    if (!curWeek) return;

    let weekId = null;
    if (planState.blockIdx === 0 && planState.weekIdx === 0) {
      weekId = curWeek.id;
    } else if (planState.weekIdx === 0) {
      const prvBlock = blocks.at(planState.blockIdx - 1);
      if (prvBlock?.weeks) {
        const prvBlockWeeks = prvBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
        weekId = prvBlockWeeks.at(prvBlock.weeks.length - 1)?.id;
      }
    } else {
      weekId = weeks.at(planState.weekIdx - 1)?.id;
    }

    if (!weekId || !planState.plan.id) return;

    rerunManipulation(params.userId, planState.plan.id, weekId)
      .then(() => setReload(prv => !prv))
      .then(() => setEditMode(false))
      .then(() => notificationHandler.success("שבוע חושב מחדש"));
  };

  const addExercise = () => {
    setWorkout(prv => {
      const newWorkout = { ...prv };
      const programExercises = newWorkout.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
      programExercises.push({
        index: newWorkout.programExercises.length,
        manipulation: "RP_NEW"
      });
      newWorkout.programExercises = programExercises;
      return newWorkout;
    });
  };

  const handleCancel = () => {
    setReload(prv => !prv);
    setEditMode(false);
  };

  const workouts = getCurrentWorkouts();
  const curWeek = getCurrentWeek();

  return (
    <Box sx={{
      minHeight: "100vh",
      backgroundColor: "#F8FAFC",
      direction: "rtl",
      paddingBottom: editMode ? "160px" : "100px"
    }}>
      <Loader loading={loading} />
      <NoteDialog approve={handleNewNote} close={() => setNewNote(false)} isOpen={newNote} />

      {/* Header */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        backgroundColor: "#FFFFFF",
        borderBottom: "1px solid #E2E8F0",
        position: "sticky",
        top: 0,
        zIndex: 100
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => navigate(`/app/clients/${params.userId}`)}
            sx={{ color: "#64748B", padding: "6px" }}
          >
            <ArrowForward sx={{ fontSize: "22px" }} />
          </IconButton>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1E293B" }}>
            תוכנית אימונים
          </Typography>
        </Box>
        <IconButton onClick={() => setNewNote(true)} sx={{ color: "#64748B" }}>
          <NoteAdd sx={{ fontSize: "20px" }} />
        </IconButton>
      </Box>

      {/* Week Summary */}
      {planState && <MobileWeekSummary planState={planState} reloadKey={reloadKey} />}

      {/* Exercise Cards */}
      <Box sx={{ padding: "12px" }}>
        {workout?.programExercises?.sort((a, b) => a.index > b.index ? 1 : -1).map((exercise, index) => {
          const prvExercise = prvWorkout?.programExercises?.find(x => x.uuid === exercise.uuid);
          return (
            <MobileExerciseCard
              key={exercise.id || exercise.uuid || index}
              exercise={exercise}
              prvExercise={prvExercise}
              workout={workout}
              setWorkout={setWorkout}
              editMode={editMode}
              openIntervals={openIntervals}
              setOpenIntervals={setOpenIntervals}
              blockIdx={planState?.blockIdx}
              weekIdx={planState?.weekIdx}
              numberOfElements={workout.programExercises.length}
              traineeId={params.userId}
            />
          );
        })}

        {/* Empty state */}
        {(!workout || !workout.programExercises?.length) && !loading && (
          <Box sx={{
            textAlign: "center",
            padding: "40px 20px",
            color: "#94A3B8"
          }}>
            <Typography>אין תרגילים באימון זה</Typography>
          </Box>
        )}
      </Box>

      {/* Action Buttons (Edit Mode) */}
      {editMode && (
        <MobileActionButtons
          onSave={handleSave}
          onCancel={handleCancel}
          onAddExercise={addExercise}
          onFinishWorkout={handleFinishWorkout}
          onResetWeek={handleResetWeek}
          onRerunManipulation={handleRerunManipulation}
          isWeekFinished={curWeek?.finished}
          isWorkoutCompleted={workout?.stateType === "COMPLETED"}
          isPlanActive={planState?.plan?.active}
          canRerunManipulation={!isAnyWorkoutCompletedOrWeekFinished() && isLastWeekFinished()}
        />
      )}

      {/* Bottom Navigation */}
      {planState && (
        <MobileBottomNav
          planState={planState}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          editMode={editMode}
          setEditMode={setEditMode}
          onNavigateForward={handleForward}
          onNavigateBackward={handleBackwards}
          workouts={workouts}
          isPlanActive={planState?.plan?.active}
          isWorkoutCompleted={workout?.stateType === "COMPLETED"}
        />
      )}
    </Box>
  );
};

export default MobileClientWeekView;
